import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { CodeSentToast } from '../../components/common/CodeSentToast';
import { PasswordInput } from '../../components/common/PasswordInput';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState<'password' | 'code'>('password');
  const [formData, setFormData] = useState({ email: '', password: '', verificationCode: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const { login, loginWithCode, sendVerificationCode, loginState, verificationState } = useAuthStore();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (loginState === 'success') navigate('/');
  }, [loginState, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      if (loginType === 'password') {
        await login({ contact: formData.email, password: formData.password });
      } else {
        await loginWithCode({ contact: formData.email, verificationCode: formData.verificationCode });
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (countdown > 0 || !formData.email) return;
    try {
      setError('');
      await sendVerificationCode({ email: formData.email, type: 'login' });
      setCountdown(60);
      setCodeSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send code');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 px-4">
      <CodeSentToast open={codeSent} onClose={() => setCodeSent(false)} />
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Sign in</h1>
          <p className="text-gray-600 mt-2">Welcome back</p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                className="input"
                placeholder="you@example.com"
                required
                value={formData.email}
                onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
              />
            </div>

            {loginType === 'password' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <PasswordInput
                  required
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={(v) => setFormData((f) => ({ ...f, password: v }))}
                />
                <div className="mt-2 flex justify-between text-sm">
                  <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500">
                    Forgot password?
                  </Link>
                  <button type="button" onClick={() => setLoginType('code')} className="text-gray-500 hover:text-gray-700">
                    Sign in with code
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification code</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    className="input flex-1"
                    placeholder="Enter the code"
                    required
                    value={formData.verificationCode}
                    onChange={(e) => setFormData((f) => ({ ...f, verificationCode: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={countdown > 0 || verificationState === 'loading'}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium min-w-[110px]"
                  >
                    {countdown > 0 ? `${countdown}s` : 'Send code'}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button type="button" onClick={() => setLoginType('password')} className="text-sm text-gray-500 hover:text-gray-700">
                    Back to password
                  </button>
                </div>
              </div>
            )}

            <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
              {isLoading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
